import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ShieldWarning, ArrowClockwise, DownloadSimple, CircleNotch, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { Footer } from "@/components/Footer";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
// The plant's own calendar day (India time on the floor's browsers), not UTC -
// a note raised at 2am IST belongs to that day, not to the day before.
const localDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const today = () => localDay(new Date());
const daysAgo = (n) => localDay(new Date(Date.now() - n * 86400000));

const STATUS_TONE = {
  pending: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  rework: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
  hold: "bg-[#F2F4F7] text-[#475467] border-[#D0D5DD]",
  partial: "bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]",
  approved: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  posted: "bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]",
  cancelled: "bg-[#F2F4F7] text-[#98A2B3] border-[#EAECF0]",
};

// Nov 4 2026, user's ask: "create a report for the Quality Decision where user
// can see information (notification no, lot id, planned qty, rp_id with name,
// lot created, qc physical accept by, decision by, date, scrap qty, remark)".
export default function QualityDecisionReportPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(today());
  const [status, setStatus] = useState("all");
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quality-decisions/report`, {
        params: { date_from: dateFrom, date_to: dateTo, status },
      });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the Quality Decision report");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, status]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/quality-decisions/report`, {
        params: { date_from: dateFrom, date_to: dateTo, status, download: true },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `quality-decisions-${dateFrom}-to-${dateTo}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const totalScrap = rows.reduce((sum, r) => sum + (Number(r.reported_scrap) || 0), 0);

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ShieldWarning size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Quality Decision Report</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 py-6 space-y-4" data-testid="quality-decision-report-page">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-lg font-bold text-[#101828]">Quality Decision Report</h1>
            <p className="text-sm text-[#475467]">Every note raised, what production reported and what quality decided</p>
          </div>
          <div className="ml-auto flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-[11px] font-bold text-[#344054]">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 bg-white" data-testid="qdn-report-date-from" />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-[#344054]">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 bg-white" data-testid="qdn-report-date-to" />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-[#344054]">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 w-40 bg-white" data-testid="qdn-report-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open only</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rework">Rework</SelectItem>
                  <SelectItem value="hold">Hold</SelectItem>
                  <SelectItem value="partial">Part cleared</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="h-9 bg-white" onClick={load} data-testid="qdn-report-refresh">
              <ArrowClockwise size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button className="h-9 bg-[#0E7C86] hover:bg-[#0B6B74]" onClick={download} disabled={downloading || !rows.length} data-testid="qdn-report-download">
              {downloading ? <CircleNotch size={14} className="animate-spin" /> : <><DownloadSimple size={14} weight="bold" className="mr-1.5" /> Excel</>}
            </Button>
            <Button variant="outline" className="h-9 bg-white" asChild data-testid="qdn-report-back">
              <Link to="/quality-decision"><ArrowLeft size={14} className="mr-1.5" /> Decisions</Link>
            </Button>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap text-xs">
          <span className="bg-white border border-[#D0D5DD] rounded-sm px-3 py-1.5" data-testid="qdn-report-count">
            Notes: <strong className="text-[#101828]">{rows.length}</strong>
          </span>
          <span className="bg-white border border-[#D0D5DD] rounded-sm px-3 py-1.5" data-testid="qdn-report-scrap-total">
            Scrap reported: <strong className="text-[#B54708]">{fmt(totalScrap)}</strong>
          </span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-[#98A2B3]"><CircleNotch size={20} className="animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-[#D0D5DD] rounded-sm py-12 text-center text-[#667085]" data-testid="qdn-report-empty">
            No Quality Decision Notes in this window.
          </div>
        ) : (
          <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto">
            <table className="w-full text-[12px] border-collapse" data-testid="qdn-report-table">
              <thead>
                <tr>
                  {["Notification No", "Lot ID", "Output Product", "Planned Qty", "Reporting Point", "Lot Created By",
                    "Reported Good", "Reported Scrap", "QC Confirmed", "Approved Scrap", "Rework", "Hold", "Status",
                    "Raised By", "Decision By", "Decision Date", "Remark"].map((h) => (
                    <th key={h} className="bg-[#F2F4F7] border-b border-[#D0D5DD] px-2 py-2 text-left text-[10px] font-bold text-[#344054] font-heading uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.notification_no} className="border-b border-[#EAECF0] last:border-0 hover:bg-[#F9FAFB]" data-testid={`qdn-report-row-${r.notification_no}`}>
                    <td className="px-2 py-1.5 font-mono font-bold text-[#175CD3] whitespace-nowrap">{r.notification_no}</td>
                    <td className="px-2 py-1.5 font-mono font-bold text-[#101828]">{r.production_lot_id}</td>
                    <td className="px-2 py-1.5 text-[#475467]">{r.main_output_product || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.planned_quantity)} {formatUnit(r.unit_code)}</td>
                    <td className="px-2 py-1.5 text-[#475467]">{r.reporting_point || "—"}</td>
                    <td className="px-2 py-1.5 text-[#475467] whitespace-nowrap">{r.lot_created_by || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.reported_good)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold text-[#B54708]">{fmt(r.reported_scrap)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold text-[#027A48]">{fmt(r.physical_accepted_quantity)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.approved_scrap_quantity)}</td>
                    {/* Nov 4 2026: the pieces quality parked - still with them until a confirm or scrap call. */}
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#B42318]">{fmt(r.rework_quantity)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#475467]">{fmt(r.hold_quantity)}</td>
                    <td className="px-2 py-1.5">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[r.status] || ""}`}>{r.status}</Badge>
                    </td>
                    <td className="px-2 py-1.5 text-[#475467] whitespace-nowrap">{r.raised_by || "—"}</td>
                    <td className="px-2 py-1.5 text-[#475467] whitespace-nowrap">{r.decided_by || "—"}</td>
                    <td className="px-2 py-1.5 text-[#667085] whitespace-nowrap">{when(r.decided_at)}</td>
                    <td className="px-2 py-1.5 text-[#475467] max-w-[220px] truncate" title={r.remark || ""}>{r.remark || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
